<template>
    <div id="edit-listing">
        <status-message></status-message>
        <div v-if="!createLocationModel && !createState && location" class="listing-content-area">
            <edit-header :is-admin="isAdmin" :edit-state="editState"
                         @open-edit="openEdit" @open-hours="showManageHours" @open-account-move="openAccountModal"
                         :selected-site="selectedSite" :hours-state="showHoursEdit"></edit-header>
            <div class="edit-subheader td-card border shadow td-flex">

                <div class="td-radio background">
                    <label for="search-history-account">
                        <input type="radio" v-model="viewState" id="search-history-account" @click="changeMode('edit')" :value="false" />
                        <span>Edit Listing</span>
                    </label>

                    <label for="search-history-user">
                        <input type="radio" v-model="viewState" id="search-history-user" @click="changeMode('view')" :value="true" />
                        <span>View Listing</span>
                    </label>
                </div>
                <div class="td-flex">
                    <a v-if="searchUrl" class="td-btn icon-only" :href="searchUrl" title="View Listing in search results"><icon name="faMagnifyingGlass" type="solid"></icon></a>
                    <a class="td-btn icon-only" :href="createUrl" title="Copy Listing"><icon name="faCopy" type="solid"></icon></a>
                    <button v-show="!showHoursEdit" class="td-btn icon-left primary-outlined" title="Change listing view" @click="toggleViewSelection"><icon name="faRepeat" type="solid"></icon>Change Template</button>
                </div>
            </div>
            <div v-show="!showHoursEdit">
                <div class="td-banner warning no-icon slim my-2" v-if="!isAdmin && editState">
                    <p>
                        <strong>Important:</strong> The information you submit will not be applied to your listing right away.
                        We will be contacting you within the next 1-2 business days to verify your information.
                        At that time, we will talk to you about the various <a href="/advertise" title="Listing Options Description" target="_blank" class="td-link">listing options on our site</a>.
                        We may, at our discretion, send you a letter at the address you specified to verify that the address is accurate.
                    </p>
                </div>
                <div class="td-banner warning no-icon my-2" v-if="isAdmin && hasPendingEdits && editState">
                    <div class="td-flex">
                        <p>Before making any edits, please make sure you have approved all pending edits submitted by the customer.</p>
                        <button class="td-btn success slim" title="Approve All Edits" @click="openApproveModal">Approve All</button>
                        <button class="td-btn danger slim" title="Decline All Edits" @click="openDeclineModal">Remove All</button>
                    </div>
                </div>
                <listing-template-selector-wrapper :listing="listingValue"
                                                   :all-services="allServices"
                                                   :enable-map="true"
                                                   :listing-template-type="listingTemplateType"
                                                   :editable="editState"
                                                   :admin-view="true"
                                                   :disable-actions="true"
                                                   :show-detail="true"
                                                   :is-admin="isAdmin"
                                                   :selected-site="selectedSite"
                                                   @open-hours="showManageHours"
                                                   @open-edit-modal="openEdit"
                                                   :override-default="overrideDefaultSite">
                </listing-template-selector-wrapper>

                <general-edit-modal v-if="showGeneralEdit"
                                    :all-services="allServices"
                                    :editStep="editStep"
                                    :edit-tab="editTab"
                                    :is-admin="isAdmin"
                                    @close-edit="closeModals"
                                    @submit-edit="updateLocation">
                </general-edit-modal>
                <marketing-edit-modal v-if="showMarketingEdit"
                                      :listing="listingValue"
                                      :all-services="allServices"
                                      :editStep="editStep"
                                      :is-admin="isAdmin"
                                      @submit-edit="updateLocation"
                                      @close-edit="closeModals"
                                      :selected-site="selectedSite"
                                      :override-default="overrideDefaultSite"
                                      :key="selectedSite"
                                      :show-section="showSection">
                </marketing-edit-modal>
            </div>
            <div v-if="showHoursEdit">
                <div class="listing-wrapper">
                    <listing :listing="listingValue"
                             :all-services="allServices"
                             :enable-map="true"
                             :skip-render="true"
                             :disable-actions="true"
                             :is-admin="isAdmin"
                             :show-detail="false">
                    </listing>
                </div>
                <edit-hours :listing="listingValue" @update-hours="updateHours" :is-admin="isAdmin"></edit-hours>
            </div>
            <edit-header :is-admin="isAdmin" :edit-state="editState" @open-edit="openEdit" @open-hours="showManageHours" :selected-site="selectedSite" :hours-state="showHoursEdit"></edit-header>
            <edit-footer v-if="isAdmin" :is-admin="isAdmin" :selected-site="selectedSite" :show-view-btn="editState || showHoursEdit" :edit-state="editState"></edit-footer>
            <modal @close="closeApproveModal" style="max-width:800px;height:auto" v-if="showApproveModal" class="delete-modal">
                <template #header>
                    <h4 class="td-h4">Approve All Edits</h4>
                </template>
                <p class="my-2"> Are you sure you want to approve all the edits selected below?</p>
                <label class="td-checkbox-label mb-4 td-text-bold" for="general-edits">
                    Apply General Edits
                    <input name="general-edits" id="general-edits" type="checkbox" v-model="applyGeneralEdits" />
                    <span></span>
                </label>
                <label class="td-checkbox-label mb-4 td-text-bold" for="marketing-edits">
                    Apply Marketing Edits
                    <input name="marketing-edits" id="marketing-edits" type="checkbox" v-model="applyMarketingEdits" />
                    <span></span>
                </label>
                <template #footer>
                    <div class="actions">
                        <button class="td-btn btn secondary-outlined" title="Cancel Approve All Edits" @click="closeApproveModal">No</button>
                        <button class="secondary td-btn" title="Approve all customer edits" @click="confirmApprove">Yes - Approve All</button>
                    </div>
                </template>
            </modal>
            <modal @close="closeAccountModal" style="max-width:800px;height:auto" v-if="showAccountModal" class="account-modal">
                <template #header>
                    <h4 class="td-h4">Move the listing to a different account</h4>
                </template>
                <p class="my-2"> Select an account from the dropdown.</p>
                <account-lookup @select-account="selectAccount"></account-lookup>
                <template #footer>
                    <div class="actions">
                        <button class="td-btn btn secondary-outlined" title="Cancel moving to another account" @click="closeAccountModal">Cancel</button>
                        <button class="secondary td-btn" title="Approve moving to another account" @click="confirmMove">Move</button>
                    </div>
                </template>
            </modal>
            <modal @close="closeDeclineModal" style="max-width:800px;height:auto" v-if="showDeclineModal" class="delete-modal">
                <template #header>
                    <h4 class="td-h4">Remove All Edits</h4>
                </template>
                <p class="my-2"> Are you sure you want to remove all the edits selected below?</p>
                <label class="td-checkbox-label mb-4 td-text-bold" for="general-edits">
                    Remove General Edits
                    <input name="general-edits" id="general-edits" type="checkbox" v-model="applyGeneralEdits" />
                    <span></span>
                </label>
                <label class="td-checkbox-label pb-4 td-text-bold" for="marketing-edits">
                    Remove Marketing Edits
                    <input name="marketing-edits" id="marketing-edits" type="checkbox" v-model="applyMarketingEdits" />
                    <span></span>
                </label>
                <template #footer>
                    <div class="actions">
                        <button class="td-btn btn secondary-outlined" title="Cancel Decline All Edits" @click="closeDeclineModal">No</button>
                        <button class="danger td-btn" title="Decline All Customer Edits" @click="confirmDecline">Yes - Remove All</button>
                    </div>
                </template>
            </modal>
            <view-selection :is-admin="isAdmin"
                            :editable="editState"
                            :location="editLocation"
                            @update-view="updateView"
                            v-show="showViewSelection"
                            @toggleViewSelection="toggleViewSelection">
            </view-selection>
        </div>
        <div v-if="createState">
            <h2 class="my-2 mb-0 td-text-center">Create Listing</h2>
            <general-edit-modal :all-services="allServices"
                                :is-admin="isAdmin"
                                :create-state="createState"
                                :account-id="accountData.id"
                                :account-number="accountData.ain"
                                :create-location="createLocationModel"
                                @submit-create="createLocation">
            </general-edit-modal>
        </div>
    </div>
</template>

<script>import script from './EditListing';
    export default script;</script>

<style lang="less">
    @import "./EditListing.less";
</style>